import React, { useEffect, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

type Scope = 'all' | 'different_number' | 'close_phone' | 'email' | 'name' | 'country' | 'formatting' | 'blank'
type Field = 'email' | 'phone' | 'name' | 'country'
type Source = 'profile' | 'kyc' | 'entered'

interface Issue {
  field: Field
  kind: string
  profileValue: string
  profileSecond?: string
  kycValue: string
}

interface ListRow {
  userId: number
  username: string
  fullName: string
  country: string
  issues: Issue[]
  sharedPhoneCount: number
  kycUpdatedAt?: string | null
  profileUpdatedAt?: string | null
}

interface Summary {
  checked: number
  withIssues: number
  differentNumber: number
  email: number
  name: number
  country: number
  formatting: number
  closePhone: number
  blank: number
}

interface AccountDetail {
  userId: number
  username: string
  kycRowCount: number
  profile: {
    email: string
    fullName: string
    firstName: string
    lastName: string
    phone: string
    phoneNumber: string
    country: string
    countryOfResidence: string
    updatedAt: string | null
  }
  kyc: {
    email: string
    fullName: string
    firstName: string
    lastName: string
    phone: string
    country: string
    status: string
    updatedAt: string | null
  }
  issues: Issue[]
  sharedPhones: Array<{ userId: number; username: string }>
  sharedEmails: Array<{ userId: number; username: string }>
}

const SCOPES: Array<{ id: Scope; label: string; count?: keyof Summary }> = [
  { id: 'all', label: 'Every difference', count: 'withIssues' },
  { id: 'different_number', label: 'Different numbers', count: 'differentNumber' },
  { id: 'close_phone', label: 'Close phone matches', count: 'closePhone' },
  { id: 'email', label: 'Email', count: 'email' },
  { id: 'name', label: 'Name', count: 'name' },
  { id: 'country', label: 'Country', count: 'country' },
  { id: 'blank', label: 'Missing', count: 'blank' },
  { id: 'formatting', label: 'Writing only', count: 'formatting' },
]

const KIND_LABEL: Record<string, string> = {
  different_number: 'Different number',
  country_code: 'Country code only',
  one_extra_digit: 'One extra digit',
  one_digit_changed: 'One digit changed',
  profile_fields_differ: 'Two profile phone fields',
  formatting: 'Different writing',
  profile_blank: 'Missing on the profile',
  kyc_blank: 'Missing on KYC',
  different: 'Different',
}

const shell: React.CSSProperties = { color: '#e5e7eb', maxWidth: '1240px' }
const card: React.CSSProperties = { background: '#111827', border: '1px solid #1f2937', borderRadius: '16px', padding: '18px' }
const inputStyle: React.CSSProperties = { background: '#0b1220', color: '#f9fafb', border: '1px solid #374151', borderRadius: '10px', padding: '10px 12px', width: '100%' }

function buttonStyle(background: string, disabled = false): React.CSSProperties {
  return { background: disabled ? '#374151' : background, color: '#fff', border: 0, borderRadius: '10px', padding: '10px 14px', cursor: disabled ? 'default' : 'pointer', fontWeight: 600 }
}

function when(value?: string | null) {
  if (!value) return 'Not recorded'
  return value.slice(0, 16).replace('T', ' ')
}

function kindColor(kind: string) {
  if (kind === 'different_number' || kind === 'different') return '#7f1d1d'
  if (kind === 'profile_blank' || kind === 'kyc_blank') return '#78350f'
  if (kind === 'formatting') return '#1e3a8a'
  return '#7c2d12'
}

const ProfileKycAlignmentDesk: React.FC = () => {
  const [scope, setScope] = useState<Scope>('different_number')
  const [query, setQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [offset, setOffset] = useState(0)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [rows, setRows] = useState<ListRow[]>([])
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<AccountDetail | null>(null)
  const [reason, setReason] = useState('')
  const [entered, setEntered] = useState<Record<string, string>>({})
  const [confirmShared, setConfirmShared] = useState(false)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async (nextScope = scope, nextQuery = appliedQuery, nextOffset = offset) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({
        view: 'list',
        scope: nextScope,
        q: nextQuery,
        limit: '40',
        offset: String(nextOffset),
      })
      const response = await fetch(`${API_BASE_URL}/api/admin/profile-kyc-alignment?${params}`, { headers: getSessionHeaders() })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not read the records')
      setSummary(body.summary || null)
      setRows(body.rows || [])
      setTotal(body.total || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the records')
    } finally {
      setLoading(false)
    }
  }

  const openAccount = async (userId: number) => {
    setError('')
    setNotice('')
    setConfirmShared(false)
    setEntered({})
    const response = await fetch(`${API_BASE_URL}/api/admin/profile-kyc-alignment?view=detail&userId=${userId}`, { headers: getSessionHeaders() })
    const body = await response.json()
    if (!response.ok) {
      setError(body.error || 'Could not open the account')
      return
    }
    setSelected(body.account)
  }

  useEffect(() => {
    load(scope, appliedQuery, offset)
  }, [scope, appliedQuery, offset])

  const saveField = async (field: Field, source: Source) => {
    if (!selected) return
    setActing(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/profile-kyc-alignment`, {
        method: 'POST',
        headers: { ...getSessionHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selected.userId,
          field,
          source,
          value: entered[field] || '',
          firstName: entered.firstName || '',
          lastName: entered.lastName || '',
          reason,
          confirmShared,
        }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not save the match')
      await openAccount(selected.userId)
      await load()
      setNotice(`${field} now matches on the profile and on KYC.`)
      if (!body.auditSaved) setNotice(`${field} now matches on the profile and on KYC. The audit row could not be written, so record this change in the incident notes.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the match')
    } finally {
      setActing(false)
    }
  }

  const issueFor = (field: Field) => selected?.issues.find(issue => issue.field === field)

  return (
    <div style={shell}>
      <div style={{ ...card, marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 8px' }}>Profile and KYC match</h2>
        <p style={{ margin: '0 0 12px', color: '#9ca3af', lineHeight: 1.5 }}>
          Email, phone, legal name, and country are compared for every account with a KYC record. A fix writes the one value you choose onto the profile and onto every KYC row for that account. The sign-in email, shares, balances, and wallets stay as they are.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {SCOPES.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => { setScope(item.id); setOffset(0) }}
              style={buttonStyle(scope === item.id ? '#1d4ed8' : '#1f2937')}
            >
              {item.label}{summary && item.count ? ` (${summary[item.count]})` : ''}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') { setAppliedQuery(query.trim()); setOffset(0) }
            }}
            placeholder="Search name, username, email, phone, or account number"
            style={inputStyle}
          />
          <button type="button" style={buttonStyle('#1d4ed8')} onClick={() => { setAppliedQuery(query.trim()); setOffset(0) }}>Search</button>
        </div>
        {summary ? <p style={{ color: '#9ca3af', margin: '12px 0 0' }}>{summary.checked} KYC accounts read. {total} accounts in this view.</p> : null}
      </div>

      {error ? <p style={{ color: '#fecaca', background: '#450a0a', borderRadius: '10px', padding: '10px 12px' }}>{error}</p> : null}
      {notice ? <p style={{ color: '#bbf7d0', background: '#052e16', borderRadius: '10px', padding: '10px 12px' }}>{notice}</p> : null}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(0, 1.1fr) minmax(320px, 0.9fr)' : '1fr', gap: '16px' }}>
        <div style={card}>
          {loading ? <p>Reading profile and KYC records…</p> : null}
          {!loading && rows.length === 0 ? <p>No accounts in this view.</p> : null}
          {rows.map(row => (
            <button
              key={row.userId}
              type="button"
              onClick={() => openAccount(row.userId)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: selected?.userId === row.userId ? '#1e3a8a' : '#0b1220', color: '#f9fafb', border: '1px solid #1f2937', borderRadius: '12px', padding: '12px', marginBottom: '8px', cursor: 'pointer' }}
            >
              <strong>{row.fullName || row.username}</strong>
              <span style={{ color: '#9ca3af' }}> · {row.username} · #{row.userId} · {row.country || 'No country'}</span>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                {row.issues.map(issue => (
                  <span key={`${issue.field}-${issue.kind}`} style={{ background: kindColor(issue.kind), borderRadius: '999px', padding: '3px 8px', fontSize: '12px' }}>
                    {issue.field}: {KIND_LABEL[issue.kind] || issue.kind}
                  </span>
                ))}
                {row.sharedPhoneCount > 0 ? <span style={{ background: '#78350f', borderRadius: '999px', padding: '3px 8px', fontSize: '12px' }}>Shared phone on {row.sharedPhoneCount}</span> : null}
              </div>
            </button>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <button type="button" style={buttonStyle('#1f2937', offset === 0)} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 40))}>Previous</button>
            <span style={{ color: '#9ca3af' }}>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + 40, total)} of ${total}`}</span>
            <button type="button" style={buttonStyle('#1f2937', offset + 40 >= total)} disabled={offset + 40 >= total} onClick={() => setOffset(offset + 40)}>Next</button>
          </div>
        </div>

        {selected ? (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>{selected.profile.fullName || selected.username}</h3>
            <p style={{ color: '#9ca3af' }}>
              {selected.username} · #{selected.userId} · KYC {selected.kyc.status || 'no status'} · {selected.kycRowCount} KYC row{selected.kycRowCount === 1 ? '' : 's'}
            </p>
            <p style={{ color: '#9ca3af' }}>Profile saved {when(selected.profile.updatedAt)} · KYC saved {when(selected.kyc.updatedAt)}</p>
            <label style={{ display: 'block', margin: '12px 0' }}>
              Reason for this correction
              <textarea value={reason} onChange={event => setReason(event.target.value)} rows={3} style={{ ...inputStyle, marginTop: '6px' }} placeholder="At least 20 characters. Say which record you checked and why this value is the right one." />
            </label>
            {(['email', 'phone', 'name', 'country'] as Field[]).map(field => {
              const issue = issueFor(field)
              const profileValue = field === 'email' ? selected.profile.email : field === 'phone' ? selected.profile.phone : field === 'name' ? selected.profile.fullName : selected.profile.country
              const kycValue = field === 'email' ? selected.kyc.email : field === 'phone' ? selected.kyc.phone : field === 'name' ? selected.kyc.fullName : selected.kyc.country
              return (
                <div key={field} style={{ borderTop: '1px solid #1f2937', paddingTop: '12px', marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <strong style={{ textTransform: 'capitalize' }}>{field}</strong>
                    <span style={{ background: issue ? kindColor(issue.kind) : '#14532d', borderRadius: '999px', padding: '3px 8px', fontSize: '12px' }}>{issue ? KIND_LABEL[issue.kind] || issue.kind : 'Matches'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', margin: '8px 0' }}>
                    <div style={{ background: '#0b1220', borderRadius: '10px', padding: '8px' }}>
                      <div style={{ color: '#9ca3af', fontSize: '12px' }}>Profile</div>
                      <div>{profileValue || 'Empty'}</div>
                      {field === 'phone' && selected.profile.phoneNumber ? <div style={{ color: '#9ca3af' }}>Second field: {selected.profile.phoneNumber}</div> : null}
                      {field === 'name' ? <div style={{ color: '#9ca3af' }}>{selected.profile.firstName} {selected.profile.lastName}</div> : null}
                      {field === 'country' && selected.profile.countryOfResidence ? <div style={{ color: '#9ca3af' }}>Residence field stays {selected.profile.countryOfResidence}</div> : null}
                    </div>
                    <div style={{ background: '#0b1220', borderRadius: '10px', padding: '8px' }}>
                      <div style={{ color: '#9ca3af', fontSize: '12px' }}>KYC</div>
                      <div>{kycValue || 'Empty'}</div>
                      {field === 'name' ? <div style={{ color: '#9ca3af' }}>{selected.kyc.firstName} {selected.kyc.lastName}</div> : null}
                    </div>
                  </div>
                  {field === 'phone' && selected.sharedPhones.length > 0 ? (
                    <label style={{ display: 'block', color: '#fde68a', marginBottom: '8px' }}>
                      <input type="checkbox" checked={confirmShared} onChange={event => setConfirmShared(event.target.checked)} /> Also on {selected.sharedPhones.map(item => `${item.username} (#${item.userId})`).join(', ')}. Confirm before saving this phone.
                    </label>
                  ) : null}
                  {field === 'email' && selected.sharedEmails.length > 0 ? <p style={{ color: '#fde68a' }}>Profile email is also on {selected.sharedEmails.map(item => item.username).join(', ')}.</p> : null}
                  {issue ? (
                    <>
                      {field === 'name' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                          <input value={entered.firstName || ''} onChange={event => setEntered({ ...entered, firstName: event.target.value })} placeholder="Correct first name" style={inputStyle} />
                          <input value={entered.lastName || ''} onChange={event => setEntered({ ...entered, lastName: event.target.value })} placeholder="Correct surname" style={inputStyle} />
                        </div>
                      ) : (
                        <input value={entered[field] || ''} onChange={event => setEntered({ ...entered, [field]: event.target.value })} placeholder={`Correct ${field}`} style={{ ...inputStyle, marginBottom: '8px' }} />
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" disabled={acting || !profileValue} style={buttonStyle('#374151', acting || !profileValue)} onClick={() => saveField(field, 'profile')}>Use profile</button>
                        <button type="button" disabled={acting || !kycValue} style={buttonStyle('#374151', acting || !kycValue)} onClick={() => saveField(field, 'kyc')}>Use KYC</button>
                        <button type="button" disabled={acting} style={buttonStyle('#1d4ed8', acting)} onClick={() => saveField(field, 'entered')}>Save corrected value</button>
                      </div>
                    </>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ProfileKycAlignmentDesk
